import { sortedVerses } from "./lib/lyrics";

// Shown on the lyrics pages while no song is selected in the session.
export const PLACEHOLDER_SONG = {
  Id: 0,
  Title: "Nothing playing yet",
  Verses: [{ Id: 0, Header: "", Lines: "The host has not picked a song yet", SongId: 0, Order: 0 }],
};

const bySortThenTitle = (a, b) => {
  const sa = a.Sort ?? -1;
  const sb = b.Sort ?? -1;
  if (sa !== sb) return sa - sb;
  return a.Title.localeCompare(b.Title);
};

/** The session's current song, or a placeholder when none is selected (or it is not loaded yet). */
export const selectCurrentSong = (state) =>
  (state.session.SongId && state.songs[state.session.SongId]) || PLACEHOLDER_SONG;

export const selectHasSong = (state) => selectCurrentSong(state).Id > 0;

/** The verse the session is on; falls back to the first verse when none/-1 is selected. */
export const selectCurrentVerse = (state) => {
  const song = selectCurrentSong(state);
  const verses = sortedVerses(song);
  return verses.find((verse) => verse.Order === state.session.VerseNum) || verses[0] || PLACEHOLDER_SONG.Verses[0];
};

/** The index of the verse on screens among the song's verses (0 when none is selected). */
export const selectCurrentVerseIndex = (state) => {
  const verses = sortedVerses(selectCurrentSong(state));
  const index = verses.findIndex((verse) => verse.Order === state.session.VerseNum);
  return index >= 0 ? index : 0;
};

/** The verse a guest is peeking at, or the live one. */
export const selectDisplayedVerse = (state) => {
  const peek = state.session.peekVerseNum;
  if (peek == null) return selectCurrentVerse(state);
  return sortedVerses(selectCurrentSong(state)).find((verse) => verse.Order === peek) || selectCurrentVerse(state);
};

/** The verse shown before the current one (same song, in tap order), for the fading tail. */
export const selectPreviousVerse = (state) => {
  const previous = state.session.previousVerse;
  if (previous == null || previous === state.session.VerseNum) return null;
  return sortedVerses(selectCurrentSong(state)).find((verse) => verse.Order === previous) || null;
};

/** The verse after the one on screens, or null on the last one. */
export const selectNextVerse = (state) => {
  const verses = sortedVerses(selectCurrentSong(state));
  return verses[selectCurrentVerseIndex(state) + 1] || null;
};

/** Whether this browser is the host of the current session (may change songs, verses and the queue). */
export const selectIsHost = (state) => state.session.isHost === true;

/** The queued songs in queue order. */
export const selectQueueSongs = (state) => Object.values(state.songs).sort(bySortThenTitle);

export const selectIsInQueue = (state, songId) => Boolean(state.songs[songId]);

/** Where the current song sits in the queue: { position (1-based, 0 when none), count }. */
export const selectQueuePosition = (state) => {
  const songs = selectQueueSongs(state);
  const index = songs.findIndex((song) => song.Id === state.session.SongId);
  return { position: index + 1, count: songs.length };
};

/**
 * The song to sing after the current one: the next unsung song in queue order, wrapping
 * around to the start; when everything has been sung, the next song regardless.
 */
export const selectNextQueueSong = (state) => {
  const songs = selectQueueSongs(state);
  const currentId = state.session.SongId;
  const index = songs.findIndex((song) => song.Id === currentId);
  const candidates = index >= 0 ? songs.slice(index + 1).concat(songs.slice(0, index)) : songs;
  return candidates.find((song) => !song.Sung) || candidates[0] || null;
};

/** The unsung song that follows the current one in queue order, without wrapping (null on the last). */
export const selectUpNextSong = (state) => {
  const songs = selectQueueSongs(state);
  const index = songs.findIndex((song) => song.Id === state.session.SongId);
  return songs.slice(index + 1).find((song) => !song.Sung) || null;
};

/** The song before the current one in queue order (the last song when none is selected). */
export const selectPreviousQueueSong = (state) => {
  const songs = selectQueueSongs(state);
  const index = songs.findIndex((song) => song.Id === state.session.SongId);
  if (index < 0) return songs[songs.length - 1] || null;
  return index > 0 ? songs[index - 1] : null;
};

export const selectIsInLibrary = (state, songId) => state.library.songs.some((song) => song.Id === songId);

/** Finds a song (possibly without verses) anywhere in the store. */
export const selectAnySong = (state, songId) =>
  state.songs[songId] ||
  state.library.songs.find((song) => song.Id === songId) ||
  state.catalog.loadedSongs?.[songId] ||
  state.catalog.searchSongs.find((song) => song.Id === songId) ||
  state.catalog.topSongs.find((song) => song.Id === songId) ||
  state.catalog.sungBefore.find((song) => song.Id === songId) ||
  state.sets.items.flatMap((set) => set.Songs).find((song) => song.Id === songId) ||
  null;

/**
 * How many semitones a song is shown transposed by on this device: the device's own choice
 * for the song, otherwise the host's key for it in the live set, otherwise none.
 */
export const selectTranspose = (state, songId) => {
  const own = state.preferences.songKeys[songId];
  if (own) return own;
  return state.songs[songId]?.Transpose || 0;
};

/** The host's key for a queued song (0 when it is not queued or unchanged). */
export const selectHostTranspose = (state, songId) => state.songs[songId]?.Transpose || 0;

// --- sets --------------------------------------------------------------------------------

export const selectSets = (state) => state.sets.items;

export const selectSetById = (state, setId) => state.sets.items.find((set) => set.Id === setId) || null;

/** The set the bookmark button adds to: the last one used, otherwise the most recent one. */
export const selectLastUsedSet = (state) =>
  selectSetById(state, state.sets.lastUsedId) || state.sets.items[0] || null;

export const selectIsInSet = (state, setId, songId) =>
  Boolean(selectSetById(state, setId)?.Songs.some((song) => song.Id === songId));

/** The name of the set on screens now: the one the session came from, or a plain label. */
export const selectLiveSetName = (state) => state.session.SetName || "Tonight’s set";

// --- requests ----------------------------------------------------------------------------

export const selectRequests = (state) => state.requests.items;

/** The songs this device asked for (or voted for) in the current session. */
export const selectAskedSongs = (state) => state.requests.mine || [];
